import type { ElementKind, LayoutElement } from "@jod/schemas";
import { Button, FloorPlan, Input, Label, PageHeader, StatusChip } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Armchair, Disc3, DoorOpen, GlassWater, Music, Square, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { useOwnerState } from "../../features/backoffice/queries.ts";
import {
  type LayoutSyncState,
  useLayoutStore,
} from "../../features/floor-plan/use-layout-store.ts";

export const Route = createFileRoute("/_app/layout")({
  component: LayoutEditor,
});

const PALETTE: { kind: ElementKind; label: string; icon: ReactNode }[] = [
  { kind: "table", label: "Table", icon: <Armchair /> },
  { kind: "stage", label: "Stage", icon: <Music /> },
  { kind: "bar", label: "Bar", icon: <GlassWater /> },
  { kind: "dancefloor", label: "Dance floor", icon: <Disc3 /> },
  { kind: "entrance", label: "Entrance", icon: <DoorOpen /> },
  { kind: "zone", label: "Zone", icon: <Square /> },
];

function LayoutEditor() {
  const { data } = useOwnerState();
  // Backend mode must target the real branch from the owner bootstrap; the
  // mock seed branch id does not exist in the database.
  const branchId = data?.branches.find((branch) => branch.venueId === data.currentVenueId)?.id;
  const store = useLayoutStore({ branchId });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Layout"
        subtitle="ผังร้าน — วางโต๊ะ เวที บาร์ แล้วลากจัดได้เลย"
        actions={
          <div className="flex items-center gap-2">
            <LayoutSyncBadge sync={store.sync} dirty={store.dirty} />
            <Button variant="ghost" size="sm" onClick={store.reset}>
              Reset
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-2">
        {PALETTE.map((p) => (
          <Button
            key={p.kind}
            variant="secondary"
            size="sm"
            className="gap-2"
            onClick={() => store.add(p.kind)}
          >
            {p.icon}
            {p.label}
          </Button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        <div className="h-[68vh] min-h-[420px]">
          <FloorPlan
            layout={store.layout}
            mode="edit"
            selectedId={store.selectedId}
            onSelect={store.select}
            onElementChange={store.update}
          />
        </div>
        <PropertiesPanel store={store} />
      </div>
    </div>
  );
}

function PropertiesPanel({ store }: { store: ReturnType<typeof useLayoutStore> }) {
  const el = store.selected;

  if (!el) {
    return (
      <aside className="rounded-[var(--radius)] border border-hairline bg-glass p-4 text-muted-foreground text-sm">
        Select an element to edit it, or add one from the palette. Drag to move, use the corner
        handles to resize, and the top handle to rotate.
      </aside>
    );
  }

  const set = (patch: Partial<LayoutElement>) => store.update(el.id, patch);

  return (
    <aside className="space-y-4 rounded-[var(--radius)] border border-hairline bg-glass p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-sm capitalize">{el.kind}</h3>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive"
          onClick={() => store.remove(el.id)}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      <Field label="Label">
        <Input value={el.label ?? ""} onChange={(e) => set({ label: e.target.value })} />
      </Field>

      {el.kind === "table" ? (
        <>
          <Field label="Code">
            <Input value={el.code} onChange={(e) => set({ code: e.target.value })} />
          </Field>
          <Field label="Shape">
            <div className="flex gap-2">
              {(["circle", "rect"] as const).map((s) => (
                <Button
                  key={s}
                  variant={el.shape === s ? "default" : "secondary"}
                  size="sm"
                  className="flex-1 capitalize"
                  onClick={() => set({ shape: s })}
                >
                  {s}
                </Button>
              ))}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Seats">
              <NumberInput value={el.seats} onValue={(seats) => set({ seats })} />
            </Field>
            <Field label="Capacity">
              <NumberInput value={el.capacity} onValue={(capacity) => set({ capacity })} />
            </Field>
          </div>
        </>
      ) : null}

      <Field label="Rotation°">
        <NumberInput value={Math.round(el.rotation)} onValue={(rotation) => set({ rotation })} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Width">
          <NumberInput value={Math.round(el.w)} onValue={(w) => set({ w: Math.max(24, w) })} />
        </Field>
        <Field label="Height">
          <NumberInput value={Math.round(el.h)} onValue={(h) => set({ h: Math.max(24, h) })} />
        </Field>
      </div>
    </aside>
  );
}

function LayoutSyncBadge({ sync, dirty }: { sync: LayoutSyncState; dirty: boolean }) {
  if (sync.mode === "mock") {
    return (
      <StatusChip tone="neutral" className="text-xs">
        {dirty ? "Saved locally" : "Demo layout"}
      </StatusChip>
    );
  }

  const toneByStatus = {
    loading: "info",
    loaded: "neutral",
    dirty: "warning",
    saving: "info",
    saved: "success",
    conflict: "warning",
    error: "neutral",
  } as const;

  const labelByStatus = {
    loading: "Loading layout…",
    loaded: "Synced",
    dirty: "Unsaved changes",
    saving: "Saving…",
    saved: "Saved",
    conflict: "Conflict — reload page",
    error: sync.error ?? "Save failed",
  } as const;

  return (
    <StatusChip tone={toneByStatus[sync.status]} className="max-w-[14rem] truncate text-xs">
      {labelByStatus[sync.status]}
    </StatusChip>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-muted-foreground text-xs">{label}</Label>
      {children}
    </div>
  );
}

function NumberInput({ value, onValue }: { value: number; onValue: (n: number) => void }) {
  return (
    <Input
      type="number"
      value={value}
      onChange={(e) => {
        const n = Number(e.target.value);
        if (!Number.isNaN(n)) onValue(n);
      }}
    />
  );
}
